/* StrainLab Grow-Tools — sl-tool-* custom elements.
   Loaded only by the four tool sections (deferred), not part of theme.js.
   All calculations run client-side; no data leaves the device. */
(() => {
  'use strict';
  if (customElements.get('sl-tool-gramm')) return;

  const $id = (id) => document.getElementById(id);

  /* ================= Tool 1: Gramm-Rechner ================= */
  class SLToolGramm extends HTMLElement {
    connectedCallback() {
      if (this._init || !$id('GcrForm')) return;
      this._init = true;

      const fmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
      const fmt2 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });

      const sliders = {
        strompreis: ['gcrStrompreis', (v) => v + ' ct/kWh'],
        lampe: ['gcrLampe', (v) => v + ' W'],
        ertrag: ['gcrErtrag', (v) => v + ' g'],
        vergleich: ['gcrVergleich', (v) => fmt2.format(v) + '/g'],
        vegTage: ['gcrVegTage', (v) => v + ' Tage'],
        blueteTage: ['gcrBlueteTage', (v) => v + ' Tage'],
        sonstW: ['gcrSonstW', (v) => v + ' W'],
        equip: ['gcrEquip', (v) => v + ' €'],
        grows: ['gcrGrows', (v) => v + (v == 1 ? ' Grow' : ' Grows')],
        verbrauch: ['gcrVerbrauch', (v) => v + ' €'],
        samen: ['gcrSamen', (v) => v + ' €']
      };

      const get = (key) => parseFloat($id(sliders[key][0]).value);
      const set = (key, v) => { $id(sliders[key][0]).value = v; paintSlider(key); };

      function paintSlider(key) {
        const el = $id(sliders[key][0]);
        const v = parseFloat(el.value);
        const pct = (v - el.min) / (el.max - el.min) * 100;
        el.style.setProperty('--fill', pct + '%');
        $id(sliders[key][0] + 'Out').textContent = sliders[key][1](v);
      }

      const presets = {
        s: { lampe: 100, equip: 300, sonstW: 25, ertrag: 60 },
        m: { lampe: 150, equip: 400, sonstW: 40, ertrag: 100 },
        l: { lampe: 300, equip: 700, sonstW: 80, ertrag: 220 }
      };

      this.querySelectorAll('.gcr__preset').forEach((btn) => {
        btn.addEventListener('click', () => {
          const p = presets[btn.dataset.preset];
          Object.keys(p).forEach((k) => set(k, p[k]));
          this.querySelectorAll('.gcr__preset').forEach((b) => b.classList.toggle('on', b === btn));
          calc();
        });
      });

      let state = {};

      const calc = () => {
        const strompreis = get('strompreis') / 100;
        const lampe = get('lampe');
        const vegT = get('vegTage');
        const blueteT = get('blueteTage');
        const sonstW = get('sonstW');
        const equip = get('equip');
        const grows = Math.max(1, get('grows'));
        const material = get('verbrauch') + get('samen');
        const ertrag = Math.max(1, get('ertrag'));
        const vergleich = get('vergleich');

        const tage = vegT + blueteT;
        const kwh = lampe / 1000 * (18 * vegT + 12 * blueteT) + sonstW / 1000 * 24 * tage;
        const strom = kwh * strompreis;
        const equipAnteil = equip / grows;
        const gesamt = strom + equipAnteil + material;
        const proGramm = gesamt / ertrag;
        const ersparnis = (vergleich - proGramm) * ertrag;

        state = { strom, equipAnteil, material, gesamt, proGramm, ersparnis, ertrag, vergleich, tage, equip };

        $id('gcrErtragHint').textContent = 'Faustregel: 0,5–1 g pro Watt — bei ' + lampe + ' W also ' + Math.round(lampe * 0.5) + '–' + lampe + ' g';

        $id('gcrOutPreis').textContent = fmt2.format(proGramm) + '/g';
        $id('gcrOutGesamt').textContent = fmt.format(gesamt);
        $id('gcrOutStrom').textContent = fmt.format(strom);
        $id('gcrOutEquip').textContent = fmt.format(equipAnteil);
        $id('gcrOutMat').textContent = fmt.format(material);

        const p1 = strom / gesamt * 100, p2 = p1 + equipAnteil / gesamt * 100;
        $id('gcrDonut').style.background = 'conic-gradient(#0e6ba8 0 ' + p1 + '%, #e03090 ' + p1 + '% ' + p2 + '%, #a3a7bd ' + p2 + '% 100%)';

        const maxG = Math.max(proGramm, vergleich);
        $id('gcrBarOwn').style.width = (proGramm / maxG * 100) + '%';
        $id('gcrBarRef').style.width = (vergleich / maxG * 100) + '%';
        $id('gcrBarOwnVal').textContent = fmt2.format(proGramm);
        $id('gcrBarRefVal').textContent = fmt2.format(vergleich);

        const saveEl = $id('gcrOutErsparnis');
        if (ersparnis >= 0) {
          saveEl.textContent = 'Du sparst ' + fmt.format(ersparnis) + ' pro Grow';
          saveEl.classList.remove('is-neg');
        } else {
          saveEl.textContent = fmt.format(Math.abs(ersparnis)) + ' teurer als der Vergleichspreis';
          saveEl.classList.add('is-neg');
        }

        drawBreakeven();
      };

      // Break-even: echter Cashflow — Equipment voll in Grow 1
      const drawBreakeven = () => {
        const N = 6, W = 300, H = 110, pad = 8;
        const own = [], ref = [];
        let maxY = 0;
        for (let n = 0; n <= N; n++) {
          const o = state.equip + n * (state.strom + state.material);
          const r = n * state.ertrag * state.vergleich;
          own.push(o); ref.push(r);
          maxY = Math.max(maxY, o, r);
        }
        if (maxY <= 0) maxY = 1;
        const pt = (n, v) => {
          const x = pad + n / N * (W - 2 * pad);
          const y = H - pad - v / maxY * (H - 2 * pad);
          return x.toFixed(1) + ',' + y.toFixed(1);
        };
        $id('gcrBeOwn').setAttribute('points', own.map((v, i) => pt(i, v)).join(' '));
        $id('gcrBeRef').setAttribute('points', ref.map((v, i) => pt(i, v)).join(' '));

        let be = -1;
        for (let i = 1; i <= N; i++) { if (ref[i] >= own[i]) { be = i; break; } }
        const dot = $id('gcrBeDot'), txt = $id('gcrBeText');
        if (be > 0) {
          const c = pt(be, own[be]).split(',');
          dot.setAttribute('cx', c[0]); dot.setAttribute('cy', c[1]);
          dot.style.display = '';
          txt.innerHTML = 'Break-even: Equipment ist nach <b>Grow ' + be + '</b> drin — danach sparst du ' + fmt.format(state.ersparnis > 0 ? state.ersparnis : 0) + ' pro Grow. <span style="opacity:.7">(gestrichelt: Kauf zum Vergleichspreis)</span>';
        } else {
          dot.style.display = 'none';
          txt.textContent = 'Mit diesen Werten lohnt sich der Eigenanbau finanziell nicht — Ertrag erhöhen oder Kosten senken.';
        }
      };

      Object.keys(sliders).forEach((k) => {
        $id(sliders[k][0]).addEventListener('input', () => {
          paintSlider(k);
          if (['lampe', 'equip', 'sonstW', 'ertrag'].indexOf(k) > -1) {
            this.querySelectorAll('.gcr__preset').forEach((b) => b.classList.remove('on'));
          }
          calc();
        });
        paintSlider(k);
      });
      calc();

      // Share-Karte (1200×630) im hellen Markenlook
      const drawCard = () => {
        const c = document.createElement('canvas');
        c.width = 1200; c.height = 630;
        const x = c.getContext('2d');

        x.fillStyle = '#fbfbfd';
        x.fillRect(0, 0, 1200, 630);
        const grad = x.createLinearGradient(0, 0, 1200, 630);
        grad.addColorStop(0, 'rgba(14,107,168,0.07)');
        grad.addColorStop(1, 'rgba(224,48,144,0.05)');
        x.fillStyle = grad;
        x.fillRect(0, 0, 1200, 630);
        x.strokeStyle = 'rgba(14,107,168,0.55)';
        x.lineWidth = 3;
        x.strokeRect(24, 24, 1152, 582);

        x.fillStyle = '#787b96';
        x.font = '600 30px "IBM Plex Mono", monospace';
        x.textAlign = 'center';
        x.fillText('MEIN GRAMM KOSTET MICH', 600, 140);

        x.fillStyle = '#0e6ba8';
        x.font = '700 150px "Space Grotesk", sans-serif';
        x.fillText(fmt2.format(state.proGramm) + '/g', 600, 310);

        x.fillStyle = '#191b26';
        x.font = '400 34px "Space Grotesk", sans-serif';
        x.fillText(state.ertrag + ' g Ertrag · ' + state.tage + ' Tage · ' + fmt.format(state.gesamt) + ' Gesamtkosten', 600, 390);

        if (state.ersparnis >= 0) {
          x.fillStyle = '#1a9e49';
          x.fillText('Ersparnis: ' + fmt.format(state.ersparnis) + ' pro Grow', 600, 448);
        }

        x.fillStyle = '#c31f78';
        x.font = '600 32px "IBM Plex Mono", monospace';
        x.fillText('strainlab.de/pages/gramm-rechner', 600, 545);
        return c;
      };

      $id('gcrDownloadBtn').addEventListener('click', () => {
        const a = document.createElement('a');
        a.download = 'mein-gramm-preis.png';
        a.href = drawCard().toDataURL('image/png');
        a.click();
      });

      $id('gcrShareBtn').addEventListener('click', () => {
        const text = 'Mein Gramm kostet mich ' + fmt2.format(state.proGramm) + ' im Eigenanbau — gerechnet mit dem StrainLab Grow-Kosten-Rechner:';
        const url = location.origin + location.pathname;
        drawCard().toBlob((blob) => {
          const file = blob ? new File([blob], 'mein-gramm-preis.png', { type: 'image/png' }) : null;
          if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator.share({ text, url, files: [file] }).catch(() => {});
          } else if (navigator.share) {
            navigator.share({ text, url }).catch(() => {});
          } else {
            navigator.clipboard.writeText(text + ' ' + url).then(() => {
              const btn = $id('gcrShareBtn');
              const old = btn.innerHTML;
              btn.innerHTML = '<i class="fa-solid fa-check" aria-hidden="true"></i> Link kopiert!';
              setTimeout(() => { btn.innerHTML = old; }, 2000);
            }).catch(() => {});
          }
        });
      });
    }
  }
  customElements.define('sl-tool-gramm', SLToolGramm);

  /* ================= Tool 2: Ernte-Rückwärtsplaner ================= */
  class SLToolErnte extends HTMLElement {
    connectedCallback() {
      if (this._init || !$id('ErpForm')) return;
      this._init = true;

      const DAY = 86400000;
      let mode = 'cured';   // 'cured' | 'harvest'
      let type = 'photo';   // 'photo' | 'auto'

      const fmtDate = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
      const fmtShort = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });

      const sliders = {
        veg: ['erpVeg', (v) => (v / 7) + ' Wochen'],
        bluete: ['erpBluete', (v) => (v / 7) + ' Wochen'],
        autoTage: ['erpAutoTage', (v) => v + ' Tage'],
        trocknung: ['erpTrocknung', (v) => v + ' Tage'],
        curing: ['erpCuring', (v) => (v / 7) + ' Wochen'],
        nass: ['erpNass', (v) => v + ' g']
      };
      const get = (key) => parseFloat($id(sliders[key][0]).value);
      function paintSlider(key) {
        const el = $id(sliders[key][0]);
        const pct = (el.value - el.min) / (el.max - el.min) * 100;
        el.style.setProperty('--fill', pct + '%');
        $id(sliders[key][0] + 'Out').textContent = sliders[key][1](parseFloat(el.value));
      }

      const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
      // Calendar arithmetic, not ms arithmetic: adding n*86400000 drifts by
      // an hour across DST changes and lands on the previous day.
      const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
      const toInput = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      const toIcs = (d) => d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');

      // Default-Zieldatum: heute + kompletter Zyklus + Puffer
      $id('erpDate').value = toInput(addDays(today0(), 7 + 28 + 56 + 10 + 21 + 7));

      let plan = [];
      let earliest = null;

      const build = () => {
        const target = $id('erpDate').value ? new Date($id('erpDate').value + 'T00:00:00') : null;
        const timeline = $id('erpTimeline');
        timeline.innerHTML = '';
        plan = [];
        if (!target || isNaN(target)) { $id('erpStatus').textContent = 'Bitte Zieldatum wählen'; return; }

        const trocknung = get('trocknung');
        const curing = get('curing');
        const keimTage = 7;

        const ernte = mode === 'cured' ? addDays(target, -(trocknung + curing)) : target;

        let keimStart, blueteStart = null, gesamtVorErnte;
        if (type === 'photo') {
          const veg = get('veg'), bluete = get('bluete');
          blueteStart = addDays(ernte, -bluete);
          keimStart = addDays(blueteStart, -(veg + keimTage));
          gesamtVorErnte = keimTage + veg + bluete;
        } else {
          gesamtVorErnte = get('autoTage');
          keimStart = addDays(ernte, -gesamtVorErnte);
        }

        const lead = gesamtVorErnte + (mode === 'cured' ? trocknung + curing : 0);
        earliest = addDays(today0(), lead);
        const tooLate = keimStart < today0();
        $id('erpWarn').style.display = tooLate ? '' : 'none';
        if (tooLate) {
          $id('erpWarnText').textContent = 'Zu knapp! Frühestes Zieldatum mit diesen Werten: ' + fmtShort.format(earliest);
        }

        const add = (date, title, desc, highlight) => { plan.push({ date, title, desc: desc || '', highlight: !!highlight }); };

        add(keimStart, 'Keimung starten', 'Samen in Anzuchterde oder Steckling setzen — konstant warm halten, z. B. mit Heizmatte.');
        add(addDays(keimStart, type === 'photo' ? 14 : 10), 'Umtopfen in den Endtopf', 'Direkt in Stofftöpfe — Autoflower möglichst nur einmal umtopfen.');
        if (type === 'photo') {
          add(addDays(blueteStart, -7), 'SCROG-Netz spannen', 'Eine Woche vor Blütestart das Spaliernetz setzen und Triebe einflechten.');
          add(blueteStart, 'Blüte einleiten (12/12)', 'Lichtzyklus auf 12 h umstellen — ab jetzt zählt die Blütedauer der Sorte.');
        } else {
          add(addDays(keimStart, 28), 'Blühbeginn (automatisch)', 'Autoflower starten von selbst — kein Lichtwechsel nötig.');
        }
        add(addDays(ernte, -14), 'Trichome checken (täglich)', 'Mit der 30×/60×-Lupe: milchig mit ersten Bernstein-Köpfchen = erntereif. <a href="/blogs/how-to-grow/trichome-erntezeitpunkt-erkennen">Guide lesen</a>.');
        add(ernte, 'ERNTE', 'Mit scharfer Schere schneiden, dunkel bei 18–20 °C aufhängen.', true);
        add(addDays(ernte, trocknung), 'Einglasen', 'Stiele knacken? Ab in luftdichte Gläser mit Hygrometer (Ziel: 58–62 % rF).');
        add(addDays(ernte, trocknung + curing), 'Fertig gecurt', 'Rauchfertig — <a href="/blogs/how-to-grow/cannabis-curing-anleitung">Curing-Guide</a> für Details. Je länger, desto runder.', true);

        const now = today0();
        plan.forEach((m) => {
          const li = document.createElement('li');
          if (m.highlight) li.classList.add('is-highlight');
          if (m.date < now) li.classList.add('is-past');
          li.innerHTML = '<span class="erp__tl-date">' + fmtDate.format(m.date) + '</span>' +
            '<span class="erp__tl-title">' + m.title + '</span>' +
            (m.desc ? '<p class="erp__tl-desc">' + m.desc + '</p>' : '');
          timeline.appendChild(li);
        });

        const diff = Math.round((keimStart - now) / DAY);
        if (tooLate) {
          $id('erpStatus').innerHTML = 'Start wäre <b>vor ' + Math.abs(diff) + ' Tagen</b> gewesen';
        } else if (diff === 0) {
          $id('erpStatus').innerHTML = '<b>Heute</b> geht\u2019s los mit der Keimung!';
        } else {
          $id('erpStatus').innerHTML = 'Start in <b>' + diff + ' Tagen</b> · Gesamtdauer ' + Math.round(lead / 7) + ' Wochen';
        }
      };

      this.querySelectorAll('[data-mode]').forEach((btn) => {
        btn.addEventListener('click', () => {
          mode = btn.dataset.mode;
          this.querySelectorAll('[data-mode]').forEach((b) => b.classList.toggle('on', b === btn));
          build();
        });
      });
      this.querySelectorAll('[data-type]').forEach((btn) => {
        btn.addEventListener('click', () => {
          type = btn.dataset.type;
          this.querySelectorAll('[data-type]').forEach((b) => b.classList.toggle('on', b === btn));
          $id('erpPhotoSliders').style.display = type === 'photo' ? '' : 'none';
          $id('erpAutoSliders').style.display = type === 'auto' ? '' : 'none';
          build();
        });
      });
      Object.keys(sliders).forEach((k) => {
        $id(sliders[k][0]).addEventListener('input', () => {
          paintSlider(k);
          if (k === 'nass') { wet(); } else { build(); }
        });
        paintSlider(k);
      });
      $id('erpDate').addEventListener('input', build);
      $id('erpWarnFix').addEventListener('click', () => {
        $id('erpDate').value = toInput(earliest);
        build();
      });

      const wet = () => {
        const nass = get('nass');
        const lo = Math.round(nass * 0.20), hi = Math.round(nass * 0.25);
        $id('erpTrockenOut').textContent = lo + '–' + hi + ' g';
      };

      const icsEscape = (s) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,');
      const stripTags = (s) => s.replace(/<[^>]*>/g, '');

      $id('erpIcsBtn').addEventListener('click', () => {
        if (!plan.length) return;
        const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//StrainLab//Ernte-Planer//DE', 'CALSCALE:GREGORIAN'];
        const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        plan.forEach((m, i) => {
          lines.push(
            'BEGIN:VEVENT',
            'UID:strainlab-ernteplan-' + Date.now() + '-' + i + '@strainlab.de',
            'DTSTAMP:' + stamp,
            'DTSTART;VALUE=DATE:' + toIcs(m.date),
            'DTEND;VALUE=DATE:' + toIcs(addDays(m.date, 1)),
            'SUMMARY:' + icsEscape('Grow-Plan: ' + m.title),
            'DESCRIPTION:' + icsEscape(stripTags(m.desc) + ' — Plan: strainlab.de/pages/ernte-planer'),
            'END:VEVENT'
          );
        });
        lines.push('END:VCALENDAR');
        const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'strainlab-ernteplan.ics';
        a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); }, 5000);
      });

      $id('erpCopyBtn').addEventListener('click', () => {
        if (!plan.length) return;
        const text = 'Mein Grow-Plan (strainlab.de/pages/ernte-planer):\n' + plan.map((m) => fmtDate.format(m.date) + ' — ' + m.title).join('\n');
        navigator.clipboard.writeText(text).then(() => {
          const btn = $id('erpCopyBtn');
          const old = btn.innerHTML;
          btn.innerHTML = '<i class="fa-solid fa-check" aria-hidden="true"></i> Kopiert!';
          setTimeout(() => { btn.innerHTML = old; }, 2000);
        }).catch(() => {});
      });

      wet();
      build();
    }
  }
  customElements.define('sl-tool-ernte', SLToolErnte);

  /* ================= Tool 3: Lux → PPFD/DLI ================= */
  class SLToolPpfd extends HTMLElement {
    connectedCallback() {
      if (this._init || !$id('LpdForm')) return;
      this._init = true;

      const fmtInt = new Intl.NumberFormat('de-DE');

      // Lux pro 1 µmol/m²/s je Spektrum (Näherungen)
      const lamps = {
        led: { div: 66, hint: 'Weiße Vollspektrum-LED: Umrechnung zuverlässig (±15 %).' },
        hps: { div: 77, hint: 'HPS/NDL: Umrechnung zuverlässig (±15 %).' },
        blurple: { div: 45, hint: '⚠ Rot-blaue LEDs: Lux ist hier physikalisch ungeeignet — nur grobe Schätzung!' },
        sun: { div: 54, hint: 'Sonnenlicht: Standardfaktor 54 lx pro µmol.' }
      };
      const phases = {
        seedling: { lo: 100, hi: 300, label: 'Sämling', el: 'lpdTgSeedling' },
        veg: { lo: 300, hi: 600, label: 'Wachstum', el: 'lpdTgVeg' },
        flower: { lo: 600, hi: 1000, label: 'Blüte', el: 'lpdTgFlower' }
      };
      const dliTargets = { seedling: [6, 15], veg: [20, 35], flower: [30, 45] };

      let lamp = 'led', phase = 'veg', hours = 18;

      const paintSlider = () => {
        const el = $id('lpdLux');
        const pct = (el.value - el.min) / (el.max - el.min) * 100;
        el.style.setProperty('--fill', pct + '%');
        $id('lpdLuxOut').textContent = fmtInt.format(el.value) + ' lx';
      };

      const calc = () => {
        const lux = parseFloat($id('lpdLux').value);
        const ppfd = lux / lamps[lamp].div;
        const dli = ppfd * 3600 * hours / 1e6;
        const p = phases[phase];

        $id('lpdLampHint').textContent = lamps[lamp].hint;
        $id('lpdPpfd').textContent = fmtInt.format(Math.round(ppfd));
        $id('lpdDli').textContent = dli.toFixed(1).replace('.', ',');

        const max = p.hi * 1.5;
        const loPct = p.lo / max * 100, hiPct = p.hi / max * 100;
        $id('lpdZoneLow').style.width = loPct + '%';
        $id('lpdZoneOk').style.width = (hiPct - loPct) + '%';
        $id('lpdZoneHigh').style.width = (100 - hiPct) + '%';
        $id('lpdMarker').style.left = 'calc(' + Math.min(99, ppfd / max * 100) + '% - 2px)';
        $id('lpdZoneOkLabel').textContent = p.lo + '–' + p.hi;

        Object.keys(phases).forEach((k) => {
          $id(phases[k].el).classList.toggle('is-current', k === phase);
        });

        const v = $id('lpdVerdict'), a = $id('lpdAdvice');
        v.classList.remove('is-low', 'is-ok', 'is-high');
        const dt = dliTargets[phase];

        if (ppfd < p.lo) {
          v.classList.add('is-low');
          v.textContent = 'Zu wenig Licht für die ' + p.label + '-Phase';
          const factor = (p.lo / ppfd);
          a.innerHTML = 'Lampe näher an die Pflanzen hängen oder höher dimmen — dir fehlen ca. ' +
            Math.round((p.lo - ppfd)) + ' µmol. ' +
            (factor > 1.8 ? 'Bei so großem Abstand zum Ziel lohnt sich langfristig eine stärkere Lampe. ' : '') +
            'Erst die Ausleuchtung prüfen (Bonus links): Ein ebenes Blätterdach per SCROG-Netz bringt oft mehr als mehr Watt.';
        } else if (ppfd <= p.hi) {
          v.classList.add('is-ok');
          v.textContent = 'Passt — optimal für die ' + p.label + '-Phase';
          a.innerHTML = 'DLI-Ziel (' + dt[0] + '–' + dt[1] + ') ' +
            (dli < dt[0] ? 'noch nicht erreicht — mehr Lichtstunden würden helfen.' : dli > dt[1] ? 'überschritten — etwas kürzer beleuchten spart Strom.' : 'ebenfalls im grünen Bereich.') +
            ' Klima im Blick behalten: mehr Licht = mehr Verdunstung — Hygrometer auf Canopy-Höhe.';
        } else {
          v.classList.add('is-high');
          v.textContent = 'Zu viel Licht für die ' + p.label + '-Phase';
          a.innerHTML = 'Dimmen oder Lampe höher hängen — Gefahr von Bleaching und Lichtstress (gerollte, gebleichte Spitzen). ' +
            'Ohne CO₂-Zugabe kann die Pflanze über ' + p.hi + ' µmol kaum verwerten.';
        }
      };

      const uniformity = () => {
        const vals = [0, 1, 2, 3, 4].map((i) => parseFloat($id('lpdP' + i).value)).filter((v) => isFinite(v) && v > 0);
        const out = $id('lpdUniformity');
        if (vals.length < 3) { out.textContent = vals.length ? 'Mindestens Mitte + 2 Ecken eintragen …' : '–'; return; }
        const min = Math.min(...vals), max = Math.max(...vals);
        const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
        const uni = Math.round(min / max * 100);
        const avgPpfd = Math.round(avg / lamps[lamp].div);
        out.innerHTML = 'Gleichmäßigkeit: <b>' + uni + ' %</b> · Ø ' + fmtInt.format(Math.round(avg)) + ' lx ≈ <b>' + avgPpfd + ' µmol</b> — ' +
          (uni >= 70
            ? 'gut ausgeleuchtet.'
            : 'die Ecken fallen stark ab. Ebenes Blätterdach per SCROG-Netz nutzt dein Licht deutlich besser.');
      };

      this.querySelectorAll('[data-lamp]').forEach((btn) => {
        btn.addEventListener('click', () => {
          lamp = btn.dataset.lamp;
          this.querySelectorAll('[data-lamp]').forEach((b) => b.classList.toggle('on', b === btn));
          calc(); uniformity();
        });
      });
      this.querySelectorAll('[data-hours]').forEach((btn) => {
        btn.addEventListener('click', () => {
          hours = parseInt(btn.dataset.hours, 10);
          this.querySelectorAll('[data-hours]').forEach((b) => b.classList.toggle('on', b === btn));
          calc();
        });
      });
      this.querySelectorAll('[data-phase]').forEach((btn) => {
        btn.addEventListener('click', () => {
          phase = btn.dataset.phase;
          this.querySelectorAll('[data-phase]').forEach((b) => b.classList.toggle('on', b === btn));
          if (phase === 'flower' && hours !== 12) {
            hours = 12;
            this.querySelectorAll('[data-hours]').forEach((b) => b.classList.toggle('on', b.dataset.hours === '12'));
          }
          calc();
        });
      });
      $id('lpdLux').addEventListener('input', () => { paintSlider(); calc(); });
      [0, 1, 2, 3, 4].forEach((i) => { $id('lpdP' + i).addEventListener('input', uniformity); });

      paintSlider();
      calc();
    }
  }
  customElements.define('sl-tool-ppfd', SLToolPpfd);

  /* ================= Tool 4: Trichom-Checker ================= */
  /* v2-Pipeline: Gray-World-Weißabgleich → Luma-Stretch → Pixel-Klassen →
     25px-Block-Filter (Hintergrund/Pistillen) → Makro-Score → 3-Stufen-Ergebnis.
     Getunt an 26 Realbildern — Analyse-Logik unverändert übernommen. */
  class SLToolTrichom extends HTMLElement {
    connectedCallback() {
      if (this._init || !$id('triDrop')) return;
      this._init = true;

      const canvas = $id('triCanvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      let originalData = null;
      let overlayData = null;
      let result = null;

      const drop = $id('triDrop');

      $id('triFile').addEventListener('change', (e) => {
        if (e.target.files && e.target.files[0]) loadFile(e.target.files[0]);
      });
      ['dragover', 'dragenter'].forEach((ev) => {
        drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-drag'); });
      });
      ['dragleave', 'drop'].forEach((ev) => {
        drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-drag'); });
      });
      drop.addEventListener('drop', (e) => {
        if (e.dataTransfer.files && e.dataTransfer.files[0]) loadFile(e.dataTransfer.files[0]);
      });
      document.addEventListener('paste', (e) => {
        if (!e.clipboardData || !document.body.contains(this)) return;
        const items = e.clipboardData.items || [];
        for (let pi = 0; pi < items.length; pi++) {
          if (items[pi].type && items[pi].type.indexOf('image/') === 0) {
            const pf = items[pi].getAsFile();
            if (pf) { e.preventDefault(); loadFile(pf); }
            return;
          }
        }
      });
      $id('triNewPhoto').addEventListener('click', () => {
        $id('triPreview').style.display = 'none';
        $id('triQuality').style.display = 'none';
        $id('triBars').style.display = 'none';
        $id('triPhase').style.display = 'none';
        $id('triActions').style.display = 'none';
        drop.style.display = '';
        $id('triFile').value = '';
        $id('triVerdict').className = 'tri__verdict';
        $id('triVerdict').textContent = 'Warte auf dein Foto …';
        $id('triAdvice').innerHTML = 'Lade ein Makrofoto deiner Blüte hoch — je näher und schärfer, desto besser. Ideal: Handy-Kamera durch eine 30×/60×-Lupe.';
      });

      const MAX_FILE_BYTES = 25 * 1024 * 1024;
      function loadError(message) {
        const verdict = $id('triVerdict');
        verdict.className = 'tri__verdict';
        verdict.textContent = message;
        $id('triFile').value = '';
      }
      function loadFile(file) {
        if (!file.type || file.type.indexOf('image/') !== 0) {
          loadError('Das ist keine Bilddatei. Bitte ein JPG, PNG oder WebP hochladen.');
          return;
        }
        if (file.size > MAX_FILE_BYTES) {
          loadError('Das Bild ist zu groß (max. 25 MB). Bitte ein kleineres Foto wählen.');
          return;
        }
        const opts = { imageOrientation: 'from-image' };
        const p = window.createImageBitmap ? createImageBitmap(file, opts).catch(() => createImageBitmap(file)) : null;
        if (p) {
          p.then(drawAndAnalyze).catch(() => legacyLoad(file));
        } else {
          legacyLoad(file);
        }
      }
      function legacyLoad(file) {
        const img = new Image();
        img.onload = () => { drawAndAnalyze(img); URL.revokeObjectURL(img.src); };
        // HEIC/HEIF and corrupt files fail to decode — say so instead of
        // leaving the dropzone silently unchanged.
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          loadError('Bildformat nicht unterstützt (z. B. HEIC). Bitte als JPG oder PNG speichern und erneut hochladen.');
        };
        img.src = URL.createObjectURL(file);
      }

      function drawAndAnalyze(img) {
        const w = img.width, h = img.height;
        const MAX = 900;
        const scale = Math.min(1, MAX / Math.max(w, h));
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        if (img.close) img.close();

        originalData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        analyze(w, h);
        drop.style.display = 'none';
        $id('triPreview').style.display = '';
        showOverlay(true);
      }

      function rgb2hsv(r, g, b) {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
        let hDeg = 0;
        if (d !== 0) {
          if (max === r) hDeg = 60 * (((g - b) / d) % 6);
          else if (max === g) hDeg = 60 * ((b - r) / d + 2);
          else hDeg = 60 * ((r - g) / d + 4);
          if (hDeg < 0) hDeg += 360;
        }
        return [hDeg, max === 0 ? 0 : d / max, max];
      }

      function percentile(hist, total, p) {
        const target = total * p;
        let acc = 0;
        for (let i = 0; i < 256; i++) { acc += hist[i]; if (acc >= target) return i / 255; }
        return 1;
      }

      function analyze(origW, origH) {
        const src = originalData;
        const d = src.data;
        const w = src.width, h = src.height;
        const n = w * h;
        let i, x, y, o, idx, bi;

        // Pass A: Gray-World-Weißabgleich (Kanalmittel, jedes 4. Pixel)
        let rSum = 0, gSum = 0, bSum = 0, cnt = 0;
        for (i = 0; i < n; i += 4) {
          o = i * 4;
          rSum += d[o]; gSum += d[o + 1]; bSum += d[o + 2]; cnt++;
        }
        const rM = rSum / cnt, gM = gSum / cnt, bM = bSum / cnt;
        const grayM = (rM + gM + bM) / 3;
        const clampGain = (m) => Math.min(1.6, Math.max(0.6, grayM / Math.max(m, 1e-6)));
        const rG = clampGain(rM), gG = clampGain(gM), bG = clampGain(bM);

        // Pass B: Helligkeits-Histogramm nach Weißabgleich
        const hist = new Uint32Array(256);
        let lumSum = 0;
        for (i = 0; i < n; i++) {
          o = i * 4;
          const mxv = Math.max(Math.min(255, d[o] * rG), Math.min(255, d[o + 1] * gG), Math.min(255, d[o + 2] * bG)) / 255;
          hist[Math.min(255, Math.round(mxv * 255))]++;
          lumSum += mxv;
        }
        const meanLum = lumSum / n;
        const lo = percentile(hist, n, 0.01), hi = percentile(hist, n, 0.99);
        const doStretch = (hi - lo) > 0.15;

        // Blockraster (25px)
        const B = 25;
        const bw = Math.ceil(w / B), bh = Math.ceil(h / B), nb = bw * bh;
        const bN = new Uint32Array(nb), bBright = new Uint32Array(nb), bEdge = new Uint32Array(nb);
        const bOrange = new Uint32Array(nb), bTri = new Uint32Array(nb), bPlant = new Uint32Array(nb);
        const cls = new Uint8Array(n); // 0 ignoriert, 1 klar, 2 milchig, 3 bernstein
        const grayRow = new Float32Array(w);
        let greenC = 0, magentaC = 0;

        // Pass C: Pixel klassifizieren + Blockstatistik + Kantendichte
        for (y = 0; y < h; y++) {
          let prevG = 0;
          for (x = 0; x < w; x++) {
            idx = y * w + x; o = idx * 4;
            const r = Math.min(255, d[o] * rG), g = Math.min(255, d[o + 1] * gG), b = Math.min(255, d[o + 2] * bG);
            const hsv = rgb2hsv(r, g, b);
            const hue = hsv[0], s = hsv[1];
            let v = hsv[2];
            if (doStretch) v = Math.min(1, Math.max(0, (v - lo) / (hi - lo)));
            const gr = (r + g + b) / 765;
            bi = ((y / B) | 0) * bw + ((x / B) | 0);
            bN[bi]++;
            const e = (x > 0 ? Math.abs(gr - prevG) : 0) + (y > 0 ? Math.abs(gr - grayRow[x]) : 0);
            if (e > 0.09) bEdge[bi]++;
            prevG = gr; grayRow[x] = gr;

            let c = 0;
            const isDark = v < 0.20;
            const isGreen = !isDark && hue >= 60 && hue <= 170 && s > 0.22;
            const isMagenta = !isDark && !isGreen && hue >= 250 && hue <= 340 && s > 0.30;
            const isOrange = !isDark && !isGreen && !isMagenta && hue >= 8 && hue < 55 && s >= 0.25 && v > 0.30;
            const isBlown = s < 0.08 && v > 0.985;
            if (isGreen) { greenC++; }
            else if (isMagenta) { magentaC++; }
            else if (isOrange) {
              bOrange[bi]++;
              if (s < 0.60) c = 3; // gedecktes Orange = Bernstein-Kandidat
            }
            else if (!isDark && !isBlown && s < 0.20 && v >= 0.68) { c = 2; }
            else if (!isDark && !isBlown && s < 0.24 && v >= 0.45) { c = 1; }
            cls[idx] = c;
            if (c >= 1) bTri[bi]++;
            if (c === 1 || c === 2) bBright[bi]++;
            if (isGreen || isDark) bPlant[bi]++;
          }
        }

        // Blockklassifikation: Hintergrund raus, Pistillen raus, Trichom-Textur zählen
        const bType = new Uint8Array(nb); // 0 valide, 1 Hintergrund, 2 Pistille/Warmfläche, 3 Textur
        let texN = 0, validN = 0;
        for (i = 0; i < nb; i++) {
          if (!bN[i]) continue;
          const fBright = bBright[i] / bN[i], fEdge = bEdge[i] / bN[i];
          const fOr = bOrange[i] / bN[i], fTri = bTri[i] / bN[i], fPl = bPlant[i] / bN[i];
          if (fBright > 0.80 && fEdge < 0.10) { bType[i] = 1; }
          else if (fOr > 0.45) { bType[i] = 2; }
          else {
            validN++;
            if (fEdge >= 0.20 && fTri >= 0.15 && fPl >= 0.06) { bType[i] = 3; texN++; }
          }
        }
        const macroScore = validN ? texN / validN : 0;

        // Pass D: Overlay einfärben + Zählung
        const out = new Uint8ClampedArray(d);
        let cT = 0, mT = 0, aT = 0, cV = 0, mV = 0, aV = 0;
        for (y = 0; y < h; y++) {
          for (x = 0; x < w; x++) {
            idx = y * w + x; o = idx * 4;
            bi = ((y / B) | 0) * bw + ((x / B) | 0);
            const pc = cls[idx];
            const bt = bType[bi];
            const counted = pc >= 1 && bt !== 1 && bt !== 2;
            if (counted) {
              if (pc === 1) { cV++; if (bt === 3) cT++; out[o] = 77; out[o + 1] = 163; out[o + 2] = 255; }
              else if (pc === 2) { mV++; if (bt === 3) mT++; out[o] = 255; out[o + 1] = 255; out[o + 2] = 255; }
              else { aV++; if (bt === 3) aT++; out[o] = 255; out[o + 1] = 159; out[o + 2] = 28; }
            } else {
              out[o] = Math.round(d[o] * 0.25 + 20);
              out[o + 1] = Math.round(d[o + 1] * 0.25 + 22);
              out[o + 2] = Math.round(d[o + 2] * 0.25 + 30);
            }
          }
        }
        overlayData = new ImageData(out, w, h);

        const tT = cT + mT + aT, tV = cV + mV + aV;
        const greenFrac = greenC / n;

        // Bildtyp bestimmen
        let level;
        if (magentaC / n > 0.25) { level = 'growlight'; }
        else if (greenFrac < 0.04 && tV / n < 0.05) { level = 'nobud'; }
        else if (macroScore >= 0.40 && tT / n >= 0.08) { level = 'macro'; }
        else if (macroScore >= 0.15 && tT / n >= 0.03 && greenFrac < 0.20) { level = 'bud'; }
        else if (tV / n >= 0.03) { level = 'far'; }
        else { level = 'unusable'; }

        // Qualitäts-Hinweise (nicht fatal)
        const quality = $id('triQuality');
        const issues = [];
        if (Math.max(origW, origH) < 500) issues.push('Sehr niedrige Auflösung — nutz die volle Kamera-Auflösung (kein Screenshot).');
        if (meanLum < 0.25) issues.push('Foto ist sehr dunkel — mehr Licht (weiße LED oder Tageslicht).');
        if (meanLum > 0.93) issues.push('Foto ist überbelichtet — Belichtung runterziehen.');
        if (level === 'bud') issues.push('Bud erkannt, aber keine echte Nahaufnahme — das Ergebnis ist nur eine <b>grobe Tendenz</b>. Für ein verlässliches Urteil: Kamera direkt durch eine 30×/60×-Lupe halten.');
        if (issues.length) {
          quality.style.display = '';
          quality.className = 'tri__quality is-warn';
          quality.innerHTML = '<b>Hinweis:</b> ' + issues.join(' ');
        } else {
          quality.style.display = 'none';
        }

        // Kein Verdict möglich → klare Ansage statt falscher Prozente
        if (level !== 'macro' && level !== 'bud') {
          result = null;
          $id('triBars').style.display = 'none';
          $id('triPhase').style.display = 'none';
          $id('triActions').style.display = 'none';
          const vEl = $id('triVerdict'), aEl = $id('triAdvice');
          vEl.className = 'tri__verdict';
          if (level === 'growlight') {
            vEl.textContent = 'Growlicht erkannt — keine Analyse möglich';
            aEl.innerHTML = 'Unter lila/rotem Licht sind Farben nicht auswertbar. Lampe kurz aus, mit weißem Licht oder Blitz neu fotografieren.';
          } else if (level === 'nobud') {
            vEl.textContent = 'Kein Bud erkannt';
            aEl.innerHTML = 'Auf dem Foto ist keine Cannabis-Blüte zu erkennen. Lade ein scharfes Foto deiner Blüte hoch — am besten eine Nahaufnahme der Blütenkelche.';
          } else if (level === 'far') {
            vEl.textContent = 'Zu weit weg — Trichome nicht erkennbar';
            aEl.innerHTML = 'Das sieht nach einer Übersichtsaufnahme aus. Trichome lassen sich erst ab Nahaufnahme beurteilen: Makro-Modus nutzen, so nah ran wie möglich, oder die Kamera durch eine 30×/60×-Lupe halten. Worauf du achten musst, steht im <a href="/blogs/how-to-grow/trichome-erntezeitpunkt-erkennen">Trichom-Guide</a>.';
          } else {
            vEl.textContent = 'Foto nicht auswertbar';
            aEl.innerHTML = 'Es wurden zu wenige auswertbare Trichom-Bereiche gefunden. Tipp: scharfe Nahaufnahme bei weißem Licht, Fokus auf die Blütenkelche (nicht die Zuckerblätter).';
          }
          return;
        }

        // Prozente aus den Textur-Blöcken
        const pC = Math.round(cT / tT * 100);
        let pM = Math.round(mT / tT * 100);
        const pA = Math.round(aT / tT * 100);
        pM += 100 - (pC + pM + pA);

        result = { clear: pC, milky: pM, amber: pA, level };
        render();
      }

      function render() {
        const rough = result.level === 'bud';
        $id('triBars').style.display = '';
        $id('triPhase').style.display = '';
        $id('triActions').style.display = '';
        $id('triBarClear').style.width = result.clear + '%';
        $id('triBarMilky').style.width = result.milky + '%';
        $id('triBarAmber').style.width = result.amber + '%';
        $id('triPctClear').textContent = result.clear + ' %';
        $id('triPctMilky').textContent = result.milky + ' %';
        $id('triPctAmber').textContent = result.amber + ' %';

        const v = $id('triVerdict'), a = $id('triAdvice');
        v.className = 'tri__verdict';
        let markerPos;
        const prefix = rough ? 'Tendenz: ' : '';

        if (result.clear > 50) {
          v.classList.add('is-early');
          v.textContent = prefix + 'Zu früh — Trichome überwiegend klar';
          a.innerHTML = 'Die Wirkstoffproduktion läuft noch. Warte mindestens 1–2 Wochen und prüfe alle 2–3 Tage erneut — Details im <a href="/blogs/how-to-grow/trichome-erntezeitpunkt-erkennen">Trichom-Guide</a>.';
          markerPos = Math.max(4, 30 - result.clear * 0.26);
        } else if (result.amber < 10) {
          v.classList.add('is-peak');
          v.textContent = prefix + 'Peak beginnt — überwiegend milchig';
          a.innerHTML = 'Jetzt geerntet wird die Wirkung eher aktiv-kopflastig. Für ein ausgewogenes Ergebnis warten, bis 10–20 % Bernstein erreicht sind. Zeitplan bis zum fertigen Glas: <a href="/pages/ernte-planer">Ernte-Planer</a>.';
          markerPos = 40 + result.milky * 0.15;
        } else if (result.amber <= 30) {
          v.classList.add('is-window');
          v.textContent = prefix + 'Erntefenster! Milchig mit ' + result.amber + ' % Bernstein';
          a.innerHTML = 'Ausgewogenes Profil — jetzt ernten. Danach zählt das <a href="/blogs/how-to-grow/cannabis-curing-anleitung">Curing</a>: luftdichte Gläser + Hygrometer bereithalten.';
          markerPos = 58 + (result.amber - 10) * 0.9;
        } else {
          v.classList.add('is-late');
          v.textContent = prefix + 'Spätes Fenster — ' + result.amber + ' % Bernstein';
          a.innerHTML = 'Die Wirkung wird zunehmend körperlich-sedierend, THC baut langsam ab. Zeitnah ernten — mit scharfer Schere und direkt in die Trocknung.';
          markerPos = Math.min(97, 78 + (result.amber - 30) * 0.4);
        }
        if (rough) {
          a.innerHTML += ' <b>Wichtig:</b> Ohne echte Nahaufnahme ist das nur eine grobe Einordnung — nicht allein danach ernten.';
        }
        $id('triPhaseMarker').style.left = 'calc(' + markerPos + '% - 2px)';
      }

      function showOverlay(on) {
        if (!originalData) return;
        ctx.putImageData(on ? overlayData : originalData, 0, 0);
        $id('triShowOverlay').classList.toggle('is-active', on);
        $id('triShowOriginal').classList.toggle('is-active', !on);
      }
      $id('triShowOverlay').addEventListener('click', () => showOverlay(true));
      $id('triShowOriginal').addEventListener('click', () => showOverlay(false));

      // Share-Karte (1200×630) im hellen Markenlook
      function drawCard() {
        const c = document.createElement('canvas');
        c.width = 1200; c.height = 630;
        const x = c.getContext('2d');
        x.fillStyle = '#fbfbfd'; x.fillRect(0, 0, 1200, 630);
        const grad = x.createLinearGradient(0, 0, 1200, 630);
        grad.addColorStop(0, 'rgba(14,107,168,0.07)');
        grad.addColorStop(1, 'rgba(224,48,144,0.05)');
        x.fillStyle = grad; x.fillRect(0, 0, 1200, 630);
        x.strokeStyle = 'rgba(14,107,168,0.55)'; x.lineWidth = 3;
        x.strokeRect(24, 24, 1152, 582);

        x.fillStyle = '#787b96'; x.textAlign = 'center';
        x.font = '600 30px "IBM Plex Mono", monospace';
        x.fillText('TRICHOM-CHECK', 600, 120);

        x.fillStyle = '#191b26';
        x.font = '700 58px "Space Grotesk", sans-serif';
        x.fillText($id('triVerdict').textContent, 600, 210);

        const rows = [
          ['Klar', result.clear, '#2f7fd6'],
          ['Milchig', result.milky, '#a3a7bd'],
          ['Bernstein', result.amber, '#f59f1c']
        ];
        rows.forEach((row, i) => {
          const y = 290 + i * 80;
          x.textAlign = 'left';
          x.fillStyle = '#4c4f68';
          x.font = '400 30px "Space Grotesk", sans-serif';
          x.fillText(row[0], 150, y + 8);
          x.fillStyle = 'rgba(25,27,38,.08)';
          x.fillRect(340, y - 16, 600, 32);
          x.fillStyle = row[2];
          x.fillRect(340, y - 16, 600 * row[1] / 100, 32);
          x.fillStyle = '#191b26';
          x.font = '700 30px "Space Grotesk", sans-serif';
          x.fillText(row[1] + ' %', 970, y + 8);
        });

        x.textAlign = 'center';
        x.fillStyle = '#c31f78';
        x.font = '600 32px "IBM Plex Mono", monospace';
        x.fillText('strainlab.de/pages/trichom-checker', 600, 560);
        return c;
      }

      $id('triDownloadBtn').addEventListener('click', () => {
        if (!result) return;
        const a = document.createElement('a');
        a.download = 'trichom-check.png';
        a.href = drawCard().toDataURL('image/png');
        a.click();
      });

      $id('triShareBtn').addEventListener('click', () => {
        if (!result) return;
        const text = 'Mein Trichom-Check: ' + result.clear + ' % klar, ' + result.milky + ' % milchig, ' + result.amber + ' % bernstein — ' + $id('triVerdict').textContent + '. Selbst checken:';
        const url = location.origin + location.pathname;
        drawCard().toBlob((blob) => {
          const file = blob ? new File([blob], 'trichom-check.png', { type: 'image/png' }) : null;
          if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator.share({ text, url, files: [file] }).catch(() => {});
          } else if (navigator.share) {
            navigator.share({ text, url }).catch(() => {});
          } else {
            navigator.clipboard.writeText(text + ' ' + url).then(() => {
              const btn = $id('triShareBtn');
              const old = btn.innerHTML;
              btn.innerHTML = '<i class="fa-solid fa-check" aria-hidden="true"></i> Kopiert!';
              setTimeout(() => { btn.innerHTML = old; }, 2000);
            }).catch(() => {});
          }
        });
      });
    }
  }
  customElements.define('sl-tool-trichom', SLToolTrichom);
})();
